// Server-Sent Events fallback for live seat-map updates.
//
// Used when the STOMP/WebSocket connection has exhausted its reconnect
// attempts (corporate proxy, mobile network blocking ws:// upgrades).
// The backend stream just signals "the seat map changed"; we refetch
// the authoritative state via the REST client on every event.

import { useEffect, useRef, useState } from 'react';
import { seatmapApi } from '../api/seatmapClient';
import type { SeatToGuestMap } from '../types';

interface UseSseSeatMapArgs {
  enabled: boolean;
  sessionId: string | null;
  onSeatMap: (map: SeatToGuestMap) => void;
}

interface UseSseSeatMapResult {
  connected: boolean;
}

function sseUrl(sessionId: string): string {
  return `/seatmap_api/sse/seatmapUpdate/${encodeURIComponent(sessionId)}`;
}

export function useSseSeatMap({
  enabled,
  sessionId,
  onSeatMap,
}: UseSseSeatMapArgs): UseSseSeatMapResult {
  const [connected, setConnected] = useState(false);

  // Keep latest callback in a ref so identity changes don't tear the
  // EventSource down on every parent render.
  const onSeatMapRef = useRef(onSeatMap);
  useEffect(() => { onSeatMapRef.current = onSeatMap; }, [onSeatMap]);

  useEffect(() => {
    if (!enabled || !sessionId) return;
    if (typeof EventSource === 'undefined') return; // unsupported browser

    const es = new EventSource(sseUrl(sessionId));

    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);

    const handler = async () => {
      const map = await seatmapApi.fetchSeatToGuestMap(sessionId);
      if (map) {
        onSeatMapRef.current(map);
        sessionStorage.setItem('seatToGuestMap', JSON.stringify(map));
      }
    };
    es.addEventListener('update', handler);

    return () => {
      es.removeEventListener('update', handler);
      es.close();
      setConnected(false);
    };
  }, [enabled, sessionId]);

  return { connected };
}
